import { PageSkeleton } from '@app/components'
import { GlobalSearch } from '@app/components/GlobalSearch'
import { useAuth } from '@app/lib/auth'
import { Navigate, useLocation } from 'react-router'
import { AppShellLayout, type NavGroup } from './AppShellLayout'

export function AdminShell() {
  const { user, isLoading, can, hasScopedRole } = useAuth()
  const location = useLocation()
  if (isLoading)
    return (
      <div className="container" style={{ paddingBlock: '3rem' }}>
        <PageSkeleton />
      </div>
    )
  if (!user)
    return (
      <Navigate
        to={`/sign-in?redirectTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    )
  if (!can('admin.access')) return <Navigate to="/portal" replace />

  const groups: NavGroup[] = [{ items: [{ to: '/admin', label: 'Dashboard', end: true }] }]
  const crm = [
    can('crm.people.read') && { to: '/admin/crm/people', label: 'People' },
    can('crm.organizations.read') && { to: '/admin/crm/organizations', label: 'Organizations' },
    can('crm.people.read') && { to: '/admin/crm/tags', label: 'Tags' },
  ].filter(Boolean) as NavGroup['items']
  if (crm.length) groups.push({ label: 'CRM', items: crm })

  const content = [
    can('content.read') && { to: '/admin/content/programs', label: 'Programs' },
    can('content.read') && { to: '/admin/content/events', label: 'Events' },
    can('content.read') && { to: '/admin/content/resources', label: 'Resources' },
    can('content.read') && { to: '/admin/content/blocks', label: 'Site content' },
  ].filter(Boolean) as NavGroup['items']
  if (content.length) groups.push({ label: 'Content', items: content })

  const apps = [
    can('applications.read') && { to: '/admin/applications', label: 'Applications' },
    can('applications.read') && {
      to: '/admin/festival/applications',
      label: 'Festival applications',
    },
    can('applications.read') && { to: '/admin/festival/booths', label: 'Festival booths' },
    can('applications.forms.write') && { to: '/admin/forms', label: 'Forms' },
    // A Reviewer grant scoped to one program still reviews through assignments.
    (can('applications.review') || can('scholarships.review') || hasScopedRole('reviewer')) && {
      to: '/admin/review',
      label: 'My reviews',
    },
    can('scholarships.manage') && { to: '/admin/scholarships', label: 'Scholarships' },
    can('mutual_aid.manage') && { to: '/admin/mutual-aid', label: 'Mutual aid' },
  ].filter(Boolean) as NavGroup['items']
  if (apps.length) groups.push({ label: 'Applications', items: apps })

  const ops = [
    can('maps.write') && { to: '/admin/maps', label: 'Maps' },
    can('volunteers.manage') && { to: '/admin/volunteers', label: 'Volunteers' },
    can('sponsorships.manage') && { to: '/admin/sponsors', label: 'Sponsors' },
    can('commerce.manage') && { to: '/admin/shop', label: 'Shop & orders' },
    can('content.write') && { to: '/admin/love', label: 'Love Letters' },
    can('content.write') && { to: '/admin/photos', label: 'Photos' },
    can('festival.alerts') && { to: '/admin/live', label: 'Festival live' },
    can('festival.checkin') && { to: '/admin/checkin', label: 'Vendor check-in' },
  ].filter(Boolean) as NavGroup['items']
  if (ops.length) groups.push({ label: 'Operations', items: ops })

  const finance = [
    can('finance.read') && { to: '/admin/finance', label: 'Transactions' },
    can('fundraising.write') && { to: '/admin/fundraising', label: 'Campaigns & funds' },
    (can('fundraising.write') || can('finance.read')) && {
      to: '/admin/fundraising/statements',
      label: 'Giving statements',
    },
    can('accounting.manage') && { to: '/admin/accounting', label: 'QuickBooks' },
    can('reports.read') && { to: '/admin/reports', label: 'Reports' },
  ].filter(Boolean) as NavGroup['items']
  if (finance.length) groups.push({ label: 'Finance', items: finance })

  const system = [
    can('communications.send') && { to: '/admin/communications', label: 'Email' },
    can('communications.send') && { to: '/admin/email-lists', label: 'Email lists' },
    can('communications.send') && { to: '/admin/newsletters', label: 'Newsletters' },
    can('admin.users.read') && { to: '/admin/users', label: 'Users & roles' },
    can('admin.audit.read') && { to: '/admin/audit', label: 'Audit log' },
    can('admin.settings.write') && { to: '/admin/settings', label: 'Settings' },
    can('admin.settings.write') && { to: '/admin/branding', label: 'Branding' },
    can('admin.settings.write') && { to: '/admin/integrations', label: 'Integrations & keys' },
    can('finance.adjust') && { to: '/admin/import/zeffy', label: 'Import from Zeffy' },
  ].filter(Boolean) as NavGroup['items']
  if (system.length) groups.push({ label: 'System', items: system })
  groups.push({
    label: 'Site',
    items: [
      { to: '/', label: 'Public site' },
      { to: '/portal', label: 'My portal' },
    ],
  })

  return <AppShellLayout title="Admin" groups={groups} extra={<GlobalSearch />} />
}
